"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, ChevronRight, Lock, Play, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { CourseMediaGallery, type CourseMediaGalleryItem } from "@/components/courses/course-media-gallery";
import { RelatedCoursesSection } from "@/components/courses/related-courses-section";
import { CourseWishlistToggle } from "@/components/courses/course-wishlist-toggle";
import type { Course, RelatedCoursesSource } from "@/types";
import { Locale, defineLocaleRecord } from "@/i18n-config";
import { cn } from "@/utils/cn";

const MissionFieldLayer = dynamic(
  () => import("@/components/courses/mission-field-layer").then((mod) => mod.MissionFieldLayer),
  { ssr: false }
);

type CourseMissionCopy = {
  back: string;
  missionDock: string;
  playCourse: string;
  openMap: string;
  courseMap: string;
  mapIntro: string;
  activeLevel: string;
  levelsCleared: (done: number, total: number) => string;
  readyToPlay: string;
  courseOverview: string;
  description: string;
  outcomes: string;
  structure: string;
  audience: string;
  lockedLevel: string;
  clearedLevel: string;
  currentLevel: string;
  playLevel: string;
  reviewLevel: string;
  closeMap: string;
  lessonLabel: (index: number) => string;
  mapLabel: string;
  currentDrop: string;
  accessReady: string;
  duration: string;
  difficulty: string;
  category: string;
  yourProgress: string;
  progress: string;
  completedLessons: string;
  remainingLessons: string;
  totalLessons: string;
  progressDescription: (currentLessonTitle: string | null, remainingLessons: number, completed: boolean) => string;
  audiencePoints: string[];
};

const courseMissionCopyByLocale = defineLocaleRecord<CourseMissionCopy>({
  en: {
    back: "Back to courses",
    missionDock: "Course overview",
    playCourse: "Open lesson",
    openMap: "Open lesson map",
    courseMap: "Lesson map",
    mapIntro: "Lessons stay visible. Completed lessons remain available for review. The current lesson is your main entry point.",
    activeLevel: "Active lesson",
    levelsCleared: (done, total) => `${done}/${total} lessons completed`,
    readyToPlay: "Ready to continue",
    courseOverview: "Course overview",
    description: "Course summary",
    outcomes: "What you'll learn",
    structure: "Lesson path",
    audience: "Built for",
    lockedLevel: "Locked",
    clearedLevel: "Completed",
    currentLevel: "Current",
    playLevel: "Open lesson",
    reviewLevel: "Review lesson",
    closeMap: "Close map",
    lessonLabel: (index) => `Lesson ${index}`,
    mapLabel: "Lesson map",
    currentDrop: "Current lesson",
    accessReady: "Access ready",
    duration: "Duration",
    difficulty: "Difficulty",
    category: "Category",
    yourProgress: "Your progress",
    progress: "Progress",
    completedLessons: "Completed lessons",
    remainingLessons: "Remaining lessons",
    totalLessons: "Total lessons",
    progressDescription: (currentLessonTitle, remainingLessons, completed) =>
      completed
        ? "All lessons are available for review from the course page or lesson map."
        : currentLessonTitle
          ? `Current lesson: ${currentLessonTitle}. ${remainingLessons} ${remainingLessons === 1 ? "lesson remains" : "lessons remain"} in this course.`
          : "Your next lesson is ready below.",
    audiencePoints: [
      "You want a guided build path.",
      "You want less noise and more focus.",
      "You want each lesson to feel like real progress.",
    ],
  },
  uk: {
    back: "Назад до курсів",
    missionDock: "Огляд курсу",
    playCourse: "Відкрити урок",
    openMap: "Відкрити мапу уроків",
    courseMap: "Мапа уроків",
    mapIntro: "Усі уроки видно одразу. Завершені уроки залишаються доступними для повторного перегляду. Поточний урок є основною точкою входу.",
    activeLevel: "Активний урок",
    levelsCleared: (done, total) => `Завершено ${done}/${total} уроків`,
    readyToPlay: "Готово продовжити",
    courseOverview: "Огляд курсу",
    description: "Опис курсу",
    outcomes: "Що ти вивчиш",
    structure: "Шлях уроків",
    audience: "Для кого це",
    lockedLevel: "Заблоковано",
    clearedLevel: "Завершено",
    currentLevel: "Поточний",
    playLevel: "Відкрити урок",
    reviewLevel: "Переглянути урок",
    closeMap: "Закрити мапу",
    lessonLabel: (index) => `Урок ${index}`,
    mapLabel: "Мапа уроків",
    currentDrop: "Поточний урок",
    accessReady: "Доступ готовий",
    duration: "Тривалість",
    difficulty: "Складність",
    category: "Категорія",
    yourProgress: "Твій прогрес",
    progress: "Прогрес",
    completedLessons: "Завершено уроків",
    remainingLessons: "Залишилось уроків",
    totalLessons: "Усього уроків",
    progressDescription: (currentLessonTitle, remainingLessons, completed) =>
      completed
        ? "Усі уроки доступні для повторного перегляду зі сторінки курсу або мапи уроків."
        : currentLessonTitle
          ? `Поточний урок: ${currentLessonTitle}. У цьому курсі залишилось ${remainingLessons} ${remainingLessons === 1 ? "урок" : "уроків"}.`
          : "Наступний урок уже готовий нижче.",
    audiencePoints: [
      "Тобі потрібен керований шлях до результату.",
      "Тобі потрібно менше шуму і більше фокусу.",
      "Ти хочеш, щоб кожен урок відчувався як реальний прогрес.",
    ],
  },
  ru: {
    back: "Назад к курсам",
    missionDock: "Обзор курса",
    playCourse: "Открыть урок",
    openMap: "Открыть карту уроков",
    courseMap: "Карта уроков",
    mapIntro: "Все уроки видны сразу. Завершенные уроки остаются доступны для повторного просмотра. Текущий урок остается основной точкой входа.",
    activeLevel: "Активный урок",
    levelsCleared: (done, total) => `Завершено ${done}/${total} уроков`,
    readyToPlay: "Готово продолжить",
    courseOverview: "Обзор курса",
    description: "Описание курса",
    outcomes: "Что вы изучите",
    structure: "Путь уроков",
    audience: "Для кого это",
    lockedLevel: "Заблокировано",
    clearedLevel: "Завершено",
    currentLevel: "Текущий",
    playLevel: "Открыть урок",
    reviewLevel: "Пересмотреть урок",
    closeMap: "Закрыть карту",
    lessonLabel: (index) => `Урок ${index}`,
    mapLabel: "Карта уроков",
    currentDrop: "Текущий урок",
    accessReady: "Доступ готов",
    duration: "Длительность",
    difficulty: "Сложность",
    category: "Категория",
    yourProgress: "Мой прогресс",
    progress: "Прогресс",
    completedLessons: "Завершено уроков",
    remainingLessons: "Осталось уроков",
    totalLessons: "Всего уроков",
    progressDescription: (currentLessonTitle, remainingLessons, completed) =>
      completed
        ? "Все уроки доступны для повторного просмотра со страницы курса или карты уроков."
        : currentLessonTitle
          ? `Текущий урок: ${currentLessonTitle}. В этом курсе осталось ${remainingLessons} ${remainingLessons === 1 ? "урок" : "уроков"}.`
          : "Следующий урок уже готов ниже.",
    audiencePoints: [
      "Тебе нужен управляемый путь к результату.",
      "Тебе нужно меньше шума и больше фокуса.",
      "Ты хочешь, чтобы каждый урок ощущался как реальный прогресс.",
    ],
  },
});

type CourseLevelNode = {
  slug: string;
  title: string;
  description: string;
  index: number;
  status: "completed" | "current" | "locked";
  href: string;
};

type CourseMissionExperienceProps = {
  lang: Locale;
  course: Course;
  mediaItems: CourseMediaGalleryItem[];
  outcomeLine: string;
  outcomeBullets: string[];
  durationMinutes: number | null;
  difficultyLabel: string;
  categoryLabel: string;
  completedLessonSlugs: string[];
  isAuthenticated: boolean;
  initialWishlistSlugs: string[];
  initialMapOpen?: boolean;
  lessonHrefTemplate?: string;
  relatedCourses: Course[];
  relatedCoursesTitle: string;
  relatedCoursesSource?: RelatedCoursesSource;
  backHref?: string;
};

function resolveLessonHref(lang: Locale, slug: string, template?: string): string {
  const baseHref = template ? template.replace("{slug}", encodeURIComponent(slug)) : `/${lang}/lessons/${slug}`;
  if (baseHref.includes("entry=")) return baseHref;
  return baseHref.includes("?") ? `${baseHref}&entry=launch` : `${baseHref}?entry=launch`;
}

function resolveCurrentIndex(lessons: Course["lessons"], completedLessonSlugs: string[]) {
  const safeLessons = lessons ?? [];
  if (safeLessons.length === 0) return 0;

  const completed = new Set(completedLessonSlugs);
  const firstIncomplete = safeLessons.findIndex((lesson) => !completed.has(lesson.slug.current));
  if (firstIncomplete !== -1) return firstIncomplete;

  return safeLessons.length - 1;
}

function buildLevelNodes(lang: Locale, course: Course, completedLessonSlugs: string[], lessonHrefTemplate?: string): CourseLevelNode[] {
  const lessons = course.lessons ?? [];
  const completed = new Set(completedLessonSlugs);
  const currentIndex = resolveCurrentIndex(lessons, completedLessonSlugs);
  const allCompleted = lessons.length > 0 && lessons.every((lesson) => completed.has(lesson.slug.current));

  return lessons.map((lesson, index) => ({
    slug: lesson.slug.current,
    title: lesson.title,
    description: (lesson.description?.trim() || "Next move ready below.").slice(0, 180),
    index,
    status:
      allCompleted || (completed.has(lesson.slug.current) && index !== currentIndex)
        ? "completed"
        : index === currentIndex
          ? "current"
          : "locked",
    href: resolveLessonHref(lang, lesson.slug.current, lessonHrefTemplate),
  }));
}

export function CourseMissionExperience({
  lang,
  course,
  mediaItems,
  outcomeLine,
  outcomeBullets: _outcomeBullets,
  durationMinutes,
  difficultyLabel,
  categoryLabel,
  completedLessonSlugs,
  isAuthenticated,
  initialWishlistSlugs,
  initialMapOpen = false,
  lessonHrefTemplate,
  relatedCourses,
  relatedCoursesTitle,
  relatedCoursesSource,
  backHref,
}: CourseMissionExperienceProps) {
  const copy = courseMissionCopyByLocale[lang];
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [mapOpen, setMapOpen] = useState(initialMapOpen);
  const levelNodes = useMemo(
    () => buildLevelNodes(lang, course, completedLessonSlugs, lessonHrefTemplate),
    [course, completedLessonSlugs, lang, lessonHrefTemplate]
  );

  const currentNode = levelNodes[resolveCurrentIndex(course.lessons, completedLessonSlugs)] ?? levelNodes[0] ?? null;
  const overviewHref = backHref ?? `/${lang}/courses`;
  const totalLessons = levelNodes.length;
  const completedLessonsCount = (course.lessons ?? []).filter((lesson) =>
    completedLessonSlugs.includes(lesson.slug.current)
  ).length;
  const remainingLessonsCount = Math.max(totalLessons - completedLessonsCount, 0);
  const progressPercent = totalLessons > 0 ? Math.round((completedLessonsCount / totalLessons) * 100) : 0;
  const courseCompleted = totalLessons > 0 && completedLessonsCount >= totalLessons;

  const updateModeQuery = useCallback(
    (nextMode: "map" | null) => {
      const params = new URLSearchParams(searchParams.toString());
      if (nextMode) {
        params.set("mode", nextMode);
      } else {
        params.delete("mode");
      }
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
    },
    [pathname, router, searchParams]
  );

  const openMap = useCallback(() => {
    setMapOpen(true);
    updateModeQuery("map");
  }, [updateModeQuery]);

  const closeMap = useCallback(() => {
    setMapOpen(false);
    updateModeQuery(null);
  }, [updateModeQuery]);

  useEffect(() => {
    if (!mapOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMap();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeMap, mapOpen]);

  return (
    <div data-testid="course-mission-root" className="relative min-h-screen overflow-hidden bg-[#f4efe3] text-[#1f2a27]">
      <MissionFieldLayer tone="course" intensity="soft" className="opacity-40" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_12%,rgba(180,83,9,0.10),transparent_28%),radial-gradient(circle_at_84%_14%,rgba(15,118,110,0.12),transparent_30%),linear-gradient(180deg,rgba(255,250,241,0.66),rgba(244,239,227,0.92))]" />

      <div className="relative z-[1] mx-auto max-w-[1280px] px-4 pb-16 pt-24 sm:px-6 sm:pt-28 lg:px-8 lg:pb-24">
        <Link
          href={overviewHref}
          className="inline-flex items-center gap-2 rounded-full border border-[#d7cab3] bg-[#fff9ef] px-4 py-2 text-sm font-medium text-[#41554e] backdrop-blur-md transition-colors hover:text-[#0f766e]"
        >
          <ArrowLeft size={16} />
          {copy.back}
        </Link>

        <section className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)] xl:items-start">
          <div className="relative overflow-hidden rounded-[32px] border border-[#d8cab2] bg-[#fffaf1] p-6 shadow-[0_20px_48px_-32px_rgba(31,44,39,0.22)] lg:p-8">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(15,118,110,0.08),transparent_24%),radial-gradient(circle_at_88%_18%,rgba(180,83,9,0.06),transparent_18%)]" />
            <div className="relative z-[1]">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#0f766e]/18 bg-[#edf9f6] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#0f766e]">
                <Sparkles size={12} />
                {copy.missionDock}
              </div>
              <div className="mt-5 min-w-0 max-w-[48rem] lg:max-w-[52rem]">
                <p className="text-sm uppercase tracking-[0.22em] text-[#60726c]">{copy.accessReady}</p>
                <div className="mt-3 flex items-start justify-between gap-4">
                  <h1 className="min-w-0 flex-1 font-[var(--font-outfit)] text-[2.6rem] leading-[0.98] tracking-[-0.04em] text-[#1f2a27] sm:text-[3.4rem]">
                    {course.title}
                  </h1>
                  <CourseWishlistToggle
                    lang={lang}
                    courseSlug={course.slug.current}
                    isAuthenticated={isAuthenticated}
                    initialWishlistSlugs={initialWishlistSlugs}
                  />
                </div>
                <p className="mt-4 max-w-[48rem] text-[1.02rem] leading-8 text-[#42554f]">{outcomeLine}</p>
              </div>

              <div className="mt-8">
                <div className="rounded-[28px] border border-[#d8cab2] bg-[#fffaf1] p-5 lg:p-6">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.16em] text-[#60726c]">{copy.mapLabel}</p>
                      <p className="mt-2 text-base font-semibold text-[#1f2a27]">{copy.courseMap}</p>
                    </div>
                    <button
                      type="button"
                      onClick={openMap}
                      className="text-sm font-medium text-[#0f766e] transition-colors hover:text-[#0d6860]"
                    >
                      {copy.openMap}
                    </button>
                  </div>
                  <div className="mt-5 space-y-4" data-testid="course-mini-map">
                    {levelNodes.map((node) => (
                      <div key={node.slug} className="flex items-center gap-3">
                        <span
                          className={cn(
                            "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border text-sm font-semibold",
                            node.status === "completed"
                              ? "border-[#0f766e]/35 bg-[#e8f6f3] text-[#0f766e]"
                              : node.status === "current"
                                ? "border-[#c28b4d]/45 bg-[#f8efdf] text-[#9c6b35]"
                                : "border-[#d8cab2] bg-white text-[#6a7a74]"
                          )}
                        >
                          {node.index + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-[#1f2a27]">{node.title}</p>
                          <p className="text-xs uppercase tracking-[0.16em] text-[#60726c]">
                            {node.status === "completed"
                              ? copy.clearedLevel
                              : node.status === "current"
                                ? copy.currentLevel
                                : copy.lockedLevel}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="overflow-hidden rounded-[30px] border border-[#d8cab2] bg-[#fffaf1] p-4 shadow-[0_18px_40px_-30px_rgba(31,44,39,0.16)]">
              <CourseMediaGallery items={mediaItems} showThumbnails thumbnailsViewport="desktop" className="space-y-4" />
            </div>

            <div className="rounded-[30px] border border-[#d8cab2] bg-[#fffaf1] p-5 shadow-[0_18px_40px_-30px_rgba(31,44,39,0.16)] lg:p-6">
              <div className="grid gap-3">
                {currentNode ? (
                  <Link
                    href={currentNode.href}
                    data-testid="course-play-course"
                    className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-[#0f766e] px-6 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#0d6860]"
                  >
                    <Play size={18} />
                    {copy.playCourse}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={openMap}
                    data-testid="course-play-course"
                    className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-full bg-[#0f766e] px-6 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#0d6860]"
                  >
                    <Play size={18} />
                    {copy.playCourse}
                  </button>
                )}
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {[
                  { label: copy.duration, value: durationMinutes !== null ? `${durationMinutes} min` : "—" },
                  { label: copy.difficulty, value: difficultyLabel },
                  { label: copy.category, value: categoryLabel },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex min-h-[116px] flex-col justify-between rounded-[22px] border border-[#d8cab2] bg-white px-4 py-4"
                  >
                    <p className="text-[9px] font-semibold uppercase tracking-[0.1em] leading-[1.35] text-[#60726c] sm:text-[10px]">
                      {item.label}
                    </p>
                    <p className="mt-5 break-words text-[1.12rem] font-semibold leading-[1.14] text-[#1f2a27] sm:text-[1.22rem]">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <div className="rounded-[28px] border border-[#d8cab2] bg-[#fffaf1] p-6 shadow-[0_18px_40px_-30px_rgba(31,44,39,0.16)] lg:p-7">
            <div className="flex flex-wrap items-end justify-between gap-5">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#60726c]">{copy.yourProgress}</p>
                <h2 className="mt-3 font-[var(--font-outfit)] text-[2rem] leading-tight text-[#1f2a27]">
                  {copy.levelsCleared(completedLessonsCount, totalLessons)}
                </h2>
                <p className="mt-4 max-w-[42rem] text-[1rem] leading-8 text-[#42554f]">
                  {copy.progressDescription(currentNode?.title ?? null, remainingLessonsCount, courseCompleted)}
                </p>
              </div>

              <div className="rounded-[22px] border border-[#d8cab2] bg-white px-5 py-4 text-right">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#60726c]">{copy.progress}</p>
                <p className="mt-2 text-[2rem] font-semibold leading-none text-[#1f2a27]">{progressPercent}%</p>
              </div>
            </div>

            <div className="mt-6 h-3 overflow-hidden rounded-full bg-[#efe6d7]">
              <div
                className="h-full rounded-full bg-[linear-gradient(90deg,#0f766e,#c28b4d)] transition-[width] duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {[
                { label: copy.completedLessons, value: completedLessonsCount },
                { label: copy.remainingLessons, value: remainingLessonsCount },
                { label: copy.totalLessons, value: totalLessons },
              ].map((item) => (
                <div key={item.label} className="rounded-[20px] border border-[#d8cab2] bg-white px-4 py-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#60726c]">{item.label}</p>
                  <p className="mt-3 text-[1.4rem] font-semibold leading-none text-[#1f2a27]">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {relatedCourses.length > 0 ? (
          <section className="mt-10 rounded-[28px] border border-[#d8cab2] bg-[#fffaf1] p-6 shadow-[0_18px_40px_-30px_rgba(31,44,39,0.16)] lg:p-7">
            <RelatedCoursesSection
              title={relatedCoursesTitle}
              courses={relatedCourses}
              lang={lang}
              source={relatedCoursesSource}
              placement="course_page"
            />
          </section>
        ) : null}
      </div>

      <AnimatePresence>
        {mapOpen ? (
          <motion.div
            key="course-map-shell"
            data-testid="course-map-shell"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] overflow-hidden bg-[#171d1a]/94"
          >
            <MissionFieldLayer tone="course" intensity="vivid" className="opacity-100" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(255,250,241,0.12),transparent_44%),linear-gradient(180deg,rgba(244,239,227,0.10),rgba(23,29,26,0.74)_24%,rgba(15,21,19,0.88)_100%)]" />
            <div className="relative z-[1] mx-auto flex h-full max-w-[1220px] flex-col px-4 py-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between gap-4 rounded-[24px] border border-[#d8cab2]/18 bg-[rgba(255,250,241,0.06)] px-4 py-3 backdrop-blur-md">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.16em] text-white/42">{copy.courseMap}</p>
                  <h2 className="mt-1 font-[var(--font-outfit)] text-[1.8rem] leading-tight text-white">{course.title}</h2>
                  <p className="mt-2 text-sm text-white/60">{copy.mapIntro}</p>
                </div>
                <button
                  type="button"
                  onClick={closeMap}
                  data-testid="course-map-close"
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#d8cab2]/18 bg-white/[0.04] text-white/72 transition-colors hover:border-[#0f766e]/32 hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="mt-4 min-h-0 flex-1 overflow-y-auto rounded-[30px] border border-[#d8cab2]/18 bg-[linear-gradient(180deg,rgba(255,250,241,0.07),rgba(255,250,241,0.03))] p-5 backdrop-blur-md lg:p-8">
                <div className="relative mx-auto max-w-[900px] pb-10 pt-4">
                  <div className="absolute bottom-8 left-5 top-8 w-px bg-white/10 md:left-1/2 md:-translate-x-1/2" />
                  <div
                    className="absolute left-5 top-8 w-px bg-[linear-gradient(180deg,#0f766e,#c28b4d)] md:left-1/2 md:-translate-x-1/2"
                    style={{
                      height: `${levelNodes.length > 1 ? Math.max(12, (resolveCurrentIndex(course.lessons, completedLessonSlugs) / (levelNodes.length - 1)) * 100) : 18}%`,
                    }}
                  />

                  <div className="space-y-8 md:space-y-10">
                    {levelNodes.map((node) => {
                      const playable = node.status !== "locked";
                      const actionLabel = node.status === "completed" ? copy.reviewLevel : copy.playLevel;

                      return (
                        <div
                          key={node.slug}
                          className={cn(
                            "relative flex items-start gap-4 md:grid md:grid-cols-[minmax(0,1fr)_56px_minmax(0,1fr)] md:gap-6",
                            node.index % 2 === 0 ? "md:[&>*:first-child]:order-1 md:[&>*:last-child]:order-3" : "md:[&>*:first-child]:order-3 md:[&>*:last-child]:order-1"
                          )}
                        >
                          <div className={cn("hidden md:block", node.index % 2 === 0 ? "" : "")} />

                          <span
                            data-testid={`course-map-node-${node.slug}`}
                            data-status={node.status}
                            className={cn(
                              "relative z-[1] mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border text-sm font-semibold md:col-start-2 md:mx-auto",
                              node.status === "completed"
                                ? "border-[#0f766e]/55 bg-[#0f766e]/18 text-[#c8ebe5]"
                                : node.status === "current"
                                  ? "border-[#c28b4d]/60 bg-[#c28b4d]/18 text-[#f1d9b6] shadow-[0_0_0_8px_rgba(194,139,77,0.10)]"
                                  : "border-[#d8cab2]/14 bg-white/[0.04] text-white/34"
                            )}
                          >
                            {node.status === "completed" ? <CheckCircle2 size={18} /> : node.index + 1}
                          </span>

                          <div
                            className={cn(
                              "min-w-0 rounded-[24px] border p-5 backdrop-blur-md md:col-span-1",
                              node.status === "locked"
                                ? "border-[#d8cab2]/14 bg-white/[0.04] text-white/52"
                                : node.status === "current"
                                  ? "border-[#c28b4d]/28 bg-[#211d18]/90 shadow-[0_24px_56px_-32px_rgba(106,72,35,0.55)]"
                                  : "border-[#0f766e]/20 bg-[#141d1b]/88"
                            )}
                          >
                            <p className="text-[11px] uppercase tracking-[0.16em] text-white/44">
                              {copy.lessonLabel(node.index + 1)} ·{" "}
                              {node.status === "completed"
                                ? copy.clearedLevel
                                : node.status === "current"
                                  ? copy.currentLevel
                                  : copy.lockedLevel}
                            </p>
                            <h3 className="mt-2 text-[1.2rem] font-semibold text-white">{node.title}</h3>
                            <p className="mt-3 text-sm leading-7 text-[#f6f1e7]/60">{node.description}</p>

                            {playable ? (
                              <div className="mt-5">
                                <Link
                                  href={node.href}
                                  data-testid={`course-map-node-${node.slug}-action`}
                                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[#d8cab2]/18 bg-white/[0.04] px-4 text-sm font-semibold text-white transition-colors hover:border-[#0f766e]/30 hover:bg-white/[0.08]"
                                >
                                  {actionLabel}
                                  <ChevronRight size={15} />
                                </Link>
                              </div>
                            ) : (
                              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#d8cab2]/16 bg-white/[0.04] px-4 py-2 text-xs font-medium text-white/46">
                                <Lock size={13} />
                                {copy.lockedLevel}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
